import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { Button, Label } from '@ipc/ipc-components';
import {
  associateHandlerRequestActionCreator,
  setAssociateHandlerSuccessStatusActionCreator,
} from '../../redux/actions/DeviceInformationActions';
import {
  getAssociateHandlerFormCheckboxSelections,
  getHandlerListByImpc, getIsHandlerListByImpcLoading,
} from '../../redux/selectors/DeviceInformationSelectors';
import ListTable from '../../../../../common/list-table/components/ListTable';
import { tableCheckItemsSetSuccess, tableLoadData } from '../../../../../common/list-table/actions/ListTableActions';
import AssociateHandlerFormColumns from './AssociateHandlerFormColumns';
import TableDefinitions from '../../../../../common/TableDefinitions';
import { AssociateHandlerFormStyle } from '../component/AssociateHandlerForm.styles';
import ConfirmationDialog from '../../../../../common/list-table/helpers/confirmationDialog';
import DateFormats from '../../../../../../elements/DateFormats';
import AddHandlerDialog from './AddHandlerDialog';
import CopyHandlerSettingsDialog from './CopyHandlerSettingsDialog';
import { getHandlersByMrdRequestActionCreator } from '../../../handlers/redux/actions/HandlersActions';
import { getAllHandlersByMrd, getIsHandLerListByMrdLoading } from '../../../handlers/redux/selectors/HandlersSelectors';
import CreateNewHandlerDialog from './CreateNewHandlerDialog';

function AssociateHandlerForm(props) {
  const {
    closeWizard,
    setAssociateHandlerStatus,
    selectedImpc,
    handlerListByImpc,
    isHandlerListByImpcLoading,
    tableLoad,
    checkItems,
    selections,
    addedMrdName,
    associateHandler,
    fetchHandlersByMrd,
    isHandLerListByMrdLoading,
    copiedHandlerSettings,
  } = props;

  const [handlerList, setHandlerList] = useState([]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [showAddHandlerDialog, setShowAddHandlerDialog] = useState(false);
  const [showCopyHandlerDialog, setShowCopyHandlerDialog] = useState(false);
  const [showCreateHandlerDialog, setShowCreateHandlerDialog] = useState(false);

  const isHandLerListByMrdLoaded = useRef(false);

  const compareHelperFunc = (a, b) => {
    if (a.handlerName.toUpperCase() < b.handlerName.toUpperCase()) {
      return -1;
    }
    if (a.handlerName.toUpperCase() > b.handlerName.toUpperCase()) {
      return 1;
    }
    return 0;
  };

  const handleAddHandler = (selectedHandlerList) => {
    const newHandlerList = [...handlerList];
    selectedHandlerList.forEach((selectedHandler) => {
      if (!newHandlerList.some((h) => h.handlerId === selectedHandler.value)) {
        newHandlerList.push({
          handlerId: selectedHandler.value,
          handlerName: selectedHandler.label,
          printReceipt: true,
          validFrom: moment(),
          validTo: null,
        });
      }
    });
    setHandlerList(newHandlerList.sort(compareHelperFunc));
  };

  const handleOnNext = async () => {
    if (selections.length === 0) {
      setAssociateHandlerStatus(true);
      return;
    }
    const selectedHandlers = handlerList.filter((handler) => selections.includes(handler.handlerId))
      .map((handler) => ({
        ...handler,
        validFrom: moment(handler.validFrom).format(DateFormats.date),
        validTo: handler.validTo === null ? null : moment(handler.validTo).format(DateFormats.date),
      }));

    await associateHandler(addedMrdName, selectedHandlers);
    setAssociateHandlerStatus(false);
  };

  const handleCopyHandlerSettings = async (mrdName) => {
    await fetchHandlersByMrd({ params: { mrdName, active: true } });
  };

  const onCreateHandler = (handler) => {
    const newHandlerList = [...handlerList,
      {
        ...handler,
        printReceipt: true,
        validFrom: moment(),
        validTo: null,
      }];
    setHandlerList(newHandlerList.sort(compareHelperFunc));
  };

  const populateCheckboxFunctions = (checkedRecords) => ({
    selectAll: () => {
      const isAllChecked = selections.length === handlerList.length;
      checkItems({
        selectedRows: isAllChecked ? [] : handlerList.map((handler) => handler.handlerId),
        tableDefinition: {
          id: TableDefinitions.HANDLERS_BY_IMPC.id,
        },
      });
    },
    rowChecked: (row) => !!checkedRecords.find((item) => item === row.original.handlerId),
    updateSelectedRecords: async (row) => {
      const isChecked = checkedRecords.find((item) => item === row.original.handlerId);
      await checkItems({
        selectedRows: isChecked
          ? checkedRecords.filter((item) => item !== row.original.handlerId)
          : [...checkedRecords, handlerList.find((item) => item.handlerId === row.original.handlerId).handlerId],
        tableDefinition: {
          id: TableDefinitions.HANDLERS_BY_IMPC.id,
        },
      });
    },
  });

  const columns = AssociateHandlerFormColumns(handlerList, setHandlerList, populateCheckboxFunctions, selections, setIsFormValid);

  useEffect(() => {
    tableLoad(TableDefinitions.HANDLERS_BY_IMPC.id, selectedImpc);
  }, []);

  useEffect(() => {
    const data = handlerListByImpc.map((el) => ({
      ...el,
      printReceipt: el.printReceipt || true,
      validFrom: el.validFrom || moment(),
      validTo: el.validTo || null,
    }));
    setHandlerList(data.sort(compareHelperFunc));
  }, [handlerListByImpc]);

  useEffect(() => {
    if (isHandLerListByMrdLoaded.current && !isHandLerListByMrdLoading) {
      const data = copiedHandlerSettings.map((el) => ({
        ...el,
        printReceipt: true,
        validFrom: moment(),
        validTo: null,
      }));
      setHandlerList(data.sort(compareHelperFunc));
    }

    isHandLerListByMrdLoaded.current = isHandLerListByMrdLoading;
  }, [isHandLerListByMrdLoading]);

  useEffect(() => {
    const isFormValidHelper = () => {
      const selectedHandlers = handlerList.filter((handler) => selections.includes(handler.handlerId));
      return !selectedHandlers.some((handler) => {
        let invalid = false;
        if (handler.validFrom === null) invalid = true;
        return invalid;
      });
    };
    setIsFormValid(isFormValidHelper);
  }, [selections, handlerList]);

  return (
    <AssociateHandlerFormStyle>
      <div className="add-mrd-container-buttons">
        <Button id="cancel" className="cancel" onClick={() => setShowConfirmationDialog(true)}>Cancel</Button>
        <Button
          id="next"
          onClick={async () => { await handleOnNext(); }}
          className={`primary-button-${!isFormValid} right-alignment`}
          disabled={!isFormValid}
        >
          Next
        </Button>
        <div className="save-label right-alignment">
          <Label>Follow the wizard to add the new MRD: </Label>
        </div>
      </div>
      <div className="add-mrd-container-body">
        <div className="associate-handler-body">
          <div className="associate-handler-body-buttons">
            <Button
              className="primary-button-false associate-handler-button"
              onClick={() => setShowAddHandlerDialog(true)}
            >
              Add Handler
            </Button>
            <Button
              className="primary-button-false associate-handler-button"
              onClick={() => setShowCopyHandlerDialog(true)}
            >
              Copy setting from other MRD
            </Button>
            <Button
              className="primary-button-false associate-handler-button"
              onClick={() => setShowCreateHandlerDialog(true)}
            >
              Create Handler
            </Button>
          </div>
          <ListTable
            listId={TableDefinitions.HANDLERS_BY_IMPC.id}
            data={handlerList}
            loading={isHandlerListByImpcLoading || isHandLerListByMrdLoading}
            columns={columns}
          />
        </div>
      </div>
      <ConfirmationDialog
        isOpen={showConfirmationDialog}
        submitButtonText="Yes"
        cancelButtonText="No"
        onClose={() => setShowConfirmationDialog(false)}
        onSubmit={() => closeWizard()}
        confirmationTitle="Are you sure you want to close the wizard? The new device will be stored without associated handlers."
      />
      <AddHandlerDialog
        openDialog={showAddHandlerDialog}
        closeDialog={() => setShowAddHandlerDialog(false)}
        onSubmit={handleAddHandler}
      />
      <CopyHandlerSettingsDialog
        openDialog={showCopyHandlerDialog}
        closeDialog={() => setShowCopyHandlerDialog(false)}
        selectedImpc={selectedImpc}
        onSubmit={handleCopyHandlerSettings}
        addedMrdName={addedMrdName}
      />
      <CreateNewHandlerDialog
        openDialog={showCreateHandlerDialog}
        closeDialog={() => setShowCreateHandlerDialog(false)}
        onSubmit={onCreateHandler}
      />
    </AssociateHandlerFormStyle>
  );
}

function mapStateToProps(state) {
  return {
    handlerListByImpc: getHandlerListByImpc(state),
    isHandlerListByImpcLoading: getIsHandlerListByImpcLoading(state),
    selections: getAssociateHandlerFormCheckboxSelections(state),
    isHandLerListByMrdLoading: getIsHandLerListByMrdLoading(state),
    copiedHandlerSettings: getAllHandlersByMrd(state),
  };
}

AssociateHandlerForm.propTypes = {
  closeWizard: PropTypes.func,
  setAssociateHandlerStatus: PropTypes.func,
  selectedImpc: PropTypes.string,
  handlerListByImpc: PropTypes.arrayOf(PropTypes.object),
  isHandlerListByImpcLoading: PropTypes.bool,
  tableLoad: PropTypes.func,
  checkItems: PropTypes.func,
  selections: PropTypes.arrayOf(PropTypes.number),
  addedMrdName: PropTypes.string,
  associateHandler: PropTypes.func,
  fetchHandlersByMrd: PropTypes.func,
  isHandLerListByMrdLoading: PropTypes.bool,
  copiedHandlerSettings: PropTypes.arrayOf(PropTypes.object),
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setAssociateHandlerStatus: setAssociateHandlerSuccessStatusActionCreator,
  associateHandler: associateHandlerRequestActionCreator,
  tableLoad: tableLoadData,
  checkItems: tableCheckItemsSetSuccess,
  fetchHandlersByMrd: getHandlersByMrdRequestActionCreator,
}, dispatch);

AssociateHandlerForm.defaultProps = {
  selections: [],
};

export default connect(mapStateToProps, mapDispatchToProps)(AssociateHandlerForm);
